// src/lib/api.ts
//
// A thin, typed client for the gateway at `services/gateway`. Every call is
// same-origin (see vite.config.ts's dev proxy; a production build sits behind
// the same reverse-origin the gateway is deployed under), so the httpOnly
// session cookie travels automatically with `credentials: 'same-origin'` — no
// bearer token is ever held in this app's memory or storage.
import {
  readDemonstrationMode,
  readIdentityProvider,
  readNotificationDelivery,
  readSettlementDispatch,
  type DemonstrationModeReport,
  type IdentityProviderReport,
  type NotificationDeliveryReport,
  type SettlementDispatchReport,
} from '@prs2/contracts'
import type {
  AccessControlMatrixResponse,
  AccountDirectoryResponse,
  AccountChange,
  AssumeAccountsResponse,
  UserAccount,
  UserAccountBody,
  UserAccountDetail,
  UserAccountListResponse,
  UserRolesResponse,
  AccountGroupsResponse,
  AgencyMappingListResponse,
  AnnouncementListResponse,
  AnnouncementPriorityValue,
  ArchiveAlertSweepResponse,
  BillReprorationsResponse,
  ReprorationAlertResponse,
  ReprorationSweepResponse,
  RetentionPolicy,
  RetentionResponse,
  WardRule,
  WardRuleListResponse,
  MsoBasisInput,
  MsoBillComparisonResponse,
  MsoCalculateResponse,
  MsoComparisonResponse,
  MsoRecordBasisResponse,
  MsoCalculation,
  MsoCalculationDetailResponse,
  MsoCalculationListResponse,
  MsoEvaluation,
  MsoRuleListResponse,
  MsoRuleVersion,
  MsoRuleVersionResponse,
  MsoRuleVersionsResponse,
  MsoTestRunResponse,
  ArchiveRunResponse,
  ArchiveSegmentsResponse,
  ArchiveStatusResponse,
  ArchiveVerifyResponse,
  AuditListResponse,
  BillAdjustmentsResponse,
  BillDetailResponse,
  BillPaymentsResponse,
  BillResponse,
  ReferBillResponse,
  ReferralCandidatesResponse,
  ReferralInboxResponse,
  BillStatusCountsResponse,
  DashboardSeriesResponse,
  EmailReportResponse,
  InterfaceAlertCountsResponse,
  BillTransitionsResponse,
  BillsListResponse,
  ContactsListResponse,
  CostEstimateResponse,
  CreateSupportTicketResponse,
  CsCardResponse,
  CustomReportFieldsResponse,
  CustomReportPreviewResponse,
  EmailTemplateListResponse,
  EmailTemplateStatusValue,
  EntitlementResponse,
  ExclusionListResponse,
  FaqAskResponse,
  FaqListResponse,
  HelpTopicResponse,
  HelpTopicsResponse,
  HspGroupListResponse,
  HspGroupMembershipListResponse,
  HspGroupStatusValue,
  IdentitiesResponse,
  InboundAdjustmentResponse,
  InboundChannelsResponse,
  InboundUploadResponse,
  MbimRecordListResponse,
  MeResponse,
  MedicalSchemeCodeListResponse,
  NotificationDeliveryAttemptsResponse,
  NotificationDeliveryListResponse,
  NotificationLogResponse,
  NotificationOutboxRunResponse,
  NotificationRequeueResponse,
  NotificationRulesResponse,
  NotificationTriggerResponse,
  OutcomeFileResponse,
  RecordsListResponse,
  IndicatorReconciliationResponse,
  ReferenceEntriesResponse,
  ReferenceParameterListResponse,
  ReferenceSupersedeResponse,
  SessionResponse,
  SettlementBatchListResponse,
  SettlementBatchMembersResponse,
  SettlementEmitResponse,
  SpecialAgencyCaseListResponse,
  SubsystemListResponse,
  SubsystemStatusValue,
  SupportTicket,
  SupportTicketDetailResponse,
  SupportTicketListResponse,
  WardClassListResponse,
  CatalogueSeriesResponse,
  IdentityProviderListResponse,
  IdentityProvider,
  AgencyMapping,
  HspGroupMembership,
  MbimRecord,
  WardClass,
  SubsystemInfo,
  MedicalSchemeCode,
  HspGroup,
  EmailTemplate,
  Announcement,
  SpecialAgencyCase,
} from './types'
import { plainErrorMessage } from './messages'

/**
 * Thrown for any non-2xx response. Carries the HTTP status and the server's own
 * `message` — every gateway error body is `{ message: string }` — so a caller
 * can render the Authority's own reason (e.g. a 403's approval-limit denial)
 * rather than a generic failure string.
 */
/**
 * ★★★ WAVE 27 — WHAT HAPPENS WHEN THE SESSION IS GONE, decided in ONE place.
 *
 * ⚠ **THE DEFECT THIS CLOSES, seen on a demonstration screen.** The gateway holds
 * sessions in memory (`InMemorySessionStore`), so a gateway restart — a deploy, a
 * crash, a developer picking up a new route — invalidates every cookie in every
 * open tab. Every screen then kept asking, every request answered `401 a session
 * is required`, and each caller rendered its own error card. The dashboard alone
 * showed FIVE: bills, the audit trail, the interface alerts, the referrals, the
 * paused list. Across the console there are **52** such surfaces.
 *
 * The officer is not looking at 52 failures. They are looking at ONE fact — they
 * are no longer signed in — and the product was reporting it as a pile of
 * unrelated breakages that a Retry button cannot fix. On a demonstration it reads
 * as a broken system; in service it sends someone to raise a support ticket about
 * a session that simply expired.
 *
 * ⚠ **WHY HERE AND NOT IN `useApi`.** `useApi` is one caller of many — exports,
 * form submits, the outbox run, every `POST` — and a rule that lives in one hook
 * is a rule the other paths do not follow. `request` is the single door every call
 * goes through, which is the same argument `fileScopePredicate` makes on the
 * server: one place to get the answer right, and no second copy to drift.
 *
 * ⚠ **THE BOOTSTRAP ROUTES ARE EXEMPT, or this loops.** `POST /api/session` is how
 * a session comes to exist and `GET /api/me` is how the app asks whether one
 * already does; a 401 from either is an ANSWER, not an expiry. Treating them as
 * expiry would fire the handler while the app is deciding whether to show the
 * login screen — which is the screen the handler navigates to.
 */
const unauthorizedListeners = new Set<() => void>()

/** Paths whose 401 is an answer rather than an expiry. See the note above. */
const SESSION_BOOTSTRAP = ['/api/session', '/api/me', '/api/identities']

/**
 * Register a listener for "this session is no longer valid". Returns an
 * unsubscribe function, so a component can register without leaking.
 */
export function onSessionExpired(listener: () => void): () => void {
  unauthorizedListeners.add(listener)
  return () => {
    unauthorizedListeners.delete(listener)
  }
}

function notifySessionExpired(path: string): void {
  if (SESSION_BOOTSTRAP.some((prefix) => path.startsWith(prefix))) return
  for (const listener of unauthorizedListeners) listener()
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * ★★ WAVE 8 (Task 8) — the gateway's own machine-readable refusal token
     * (`reason`), where it sent one, and `null` where it did not.
     *
     * ⚠ **Added because a screen that has to tell FIVE refusals apart should not
     * be reading English.** ("four" here until 2026-08-20; the route refuses 400 ×2,
     * 403, 404 and 409, and `SettlementBatchesPage.dom.test.tsx` has asserted five
     * distinct headings since the control landed.) It refuses five ways and each
     * needs a different next action from the officer — correct the id, switch
     * identity, wait for the ingest — so `SettlementBatchesPage` frames each one
     * differently. Keying that off `message` would mean matching on prose the
     * server is free to reword; keying it off `status` alone collapses the two
     * 400s. The token is what the route already publishes for exactly this.
     *
     * ⚠ It is a LABEL, never a substitute for {@link Error.message}. The sentence
     * the officer reads is always the server's own, rendered verbatim; this only
     * decides which frame it is rendered in. A `reason` a build does not
     * recognise must therefore still produce a readable refusal, and it does —
     * see `OUTCOME_REFUSAL_FRAME`'s fallback.
     */
    readonly reason: string | null = null,
    /**
     * The rest of the refusal body, minus `message` and `reason` — the fields a
     * route sends so a screen does not have to parse a sentence to recover a
     * fact it already published (`deliveryState` on this route's 409).
     *
     * ⚠ **`unknown` values, deliberately.** This is a parsed JSON body from the
     * network; a caller narrows what it reads. Declaring it as anything more
     * specific here would be a cast pretending to be a type, which is the shape
     * `readSettlementDispatch` exists to refuse one field over.
     */
    readonly detail: Readonly<Record<string, unknown>> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** True for a network failure (gateway unreachable) as opposed to an HTTP error response. */
export function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError
}

/**
 * A file name reduced to something that can legally BE a header value.
 *
 * ⚠ Not defensive decoration. `fetch` throws `TypeError` on a header value
 * carrying a code point outside Latin-1 or a control character, so a
 * Chinese-named or emoji-named file would fail the upload in the BROWSER, with
 * a message about an invalid header value and nothing about the file — the
 * least useful possible failure. The name decides nothing on the server (it is
 * recorded for an operator and the on-channel name is derived independently),
 * so reducing it costs nothing real.
 *
 * ⚠ Bounded here as well as on the server. `MAX_RECORDED_UPLOAD_NAME_LENGTH`
 * truncates what reaches `audit.event`; this stops a 4 KB name being sent at
 * all, since some proxies refuse an oversized header before the gateway sees it.
 */
function asciiHeaderValue(name: string): string {
  const reduced = name.replace(/[^\x20-\x7e]/g, '_').slice(0, 200)
  return reduced.length === 0 ? 'upload' : reduced
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      ...(init?.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  let body: unknown
  if (text.length > 0) {
    try {
      body = JSON.parse(text)
    } catch {
      // A non-JSON body (e.g. a proxy's own HTML error page) is reported as-is
      // rather than swallowed — see the `!response.ok` branch below.
      body = undefined
    }
  }

  if (!response.ok) {
    // ⚠ BEFORE the throw, so every caller's `catch` still runs exactly as it did
    // — this adds a reaction, it does not replace the error. A caller that wants
    // to handle its own 401 (there is one: the sign-in form) still can.
    if (response.status === 401) notifySessionExpired(path)
    const fields: Record<string, unknown> =
      body !== undefined && typeof body === 'object' && body !== null ? { ...(body as Record<string, unknown>) } : {}
    // ★ F-30: a bare "internal error" or "request failed with HTTP 500" is replaced by a sentence that says what to do.
    const message = plainErrorMessage(
      response.status,
      'message' in fields ? String(fields['message']) : `request failed with HTTP ${response.status}`,
    )
    // ⚠ A `reason` that is not a string is treated as absent rather than
    // stringified. A caller switches on this value, and `String(undefined)`
    // would hand it the literal token `"undefined"` to match against.
    const reason = typeof fields['reason'] === 'string' ? fields['reason'] : null
    // The two fields above are removed so `detail` is exactly "what else the
    // route said" — a caller reading it never has to know which keys were
    // already promoted to their own arguments.
    delete fields['message']
    delete fields['reason']
    throw new ApiError(response.status, message, reason, fields)
  }

  return body as T
}

/**
 * The pair of fields every response that describes the GATEWAY ITSELF carries —
 * `/api/identities`, `/api/me` and `POST /api/session` — narrowed here rather
 * than trusted from an `interface`.
 *
 * ══ WHY THIS EXISTS AT THE BOUNDARY AND NOT AT THE CALL SITES ══
 *
 * `request<T>` above is a `JSON.parse` and a cast. A field the server does not
 * send is `undefined` at runtime no matter how confidently `types.ts` declares
 * it a `string`, and that is not a theoretical gap: `identityProvider` decides
 * whether the officer is told that nothing on screen is real, and a response
 * that omitted it produced `undefined` → "not reported yet" → **no badge and no
 * warning at all**, on a build that may well be the passwordless demo stub over
 * synthetic seed data.
 *
 * ⚠ **This is the mechanism, not a special case for one field.** The synthetic-
 * data notice makes claims about the gateway, each claim needs a field to license
 * it, and every such field arrives through the same cast — so every such field is
 * read here. `settlementDispatch` joined `identityProvider` for exactly that
 * reason (S-016) rather than being checked at its own call sites; a second
 * mechanism beside this one is how the two would drift.
 *
 * The three call sites could each have checked. They would each have had to
 * keep checking, and the fourth one — added by whoever needs the field next —
 * would not have. Narrowing here means the unvalidated `string` never exists:
 * `identityProvider` leaves this module as an {@link IdentityProviderReport},
 * which `JSON.parse` cannot produce and which `environmentBanner` is the only
 * consumer of. A future response type that declares a bare `string` cannot be
 * passed to it without a compile error, which is the property the call-site fix
 * did not have.
 *
 * ⚠ `environment` is narrowed on the same principle even though it no longer
 * decides anything — it is quoted verbatim into an officer-facing sentence, and
 * a `number` reaching that sentence through a cast would be the same class of
 * hole with a smaller blast radius.
 */
interface SelfReport {
  /** The gateway's own `APP_ENV`, or `null` when it did not report a string. */
  readonly environment: string | null
  /** What this response said about the identity provider — see the contract. */
  readonly identityProvider: IdentityProviderReport
  /**
   * What this response said about whether anything done here can reach a Payment
   * Agency — see `SettlementDispatch` in `@prs2/contracts`.
   *
   * ⚠ **The SECOND half of the same sentence, and it is narrowed here for the
   * same reason as the first — not for symmetry.** The notice claims *"nothing
   * you do here reaches a live payment agency"*. That clause is now licensed by
   * this field and by nothing else (S-016), so a response that OMITS it must be
   * able to withdraw the licence. It cannot do that through a `string` over a
   * `JSON.parse` cast: an older gateway behind a newer bundle, or a proxy that
   * strips the field, would arrive as `undefined` and — read as a bare string —
   * would be indistinguishable from a gateway that had never been asked.
   *
   * `readSettlementDispatch` collapses absent, `''`, JSON-`null` and non-string
   * into `reported: null` — *"it answered and did not say"* — while the EXISTENCE
   * of the report is what says a response arrived at all. Same shape as
   * `identityProvider`, same reason, one boundary.
   */
  readonly settlementDispatch: SettlementDispatchReport
  /**
   * ★ TASK 5a. What this response said about whether this gateway is a live
   * demonstration of the production build — see `IdentitiesResponse.demonstrationMode`
   * in `./types.ts` and `packages/contracts/src/demonstration-mode.ts`.
   *
   * ⚠ Narrowed here for the same reason as the other two: a demonstration
   * banner is a reassurance-shaped statement ("no record here reaches a real
   * counterparty"), and a bare `string` over a `JSON.parse` cast would let an
   * absent field, a non-string, or an empty string arrive as `undefined` and
   * be mistaken for either "on" or "off" by whichever call site read it next.
   */
  readonly demonstrationMode: DemonstrationModeReport
  /**
   * ★★ THE FINAL INTEGRATION REVIEW of the 2026-08-15 notification-delivery
   * wave. What this response said about whether anything queued here can leave
   * the building as an email — see `IdentitiesResponse.notificationDelivery` in
   * `./types.ts` and `packages/contracts/src/notification-delivery.ts`.
   *
   * ⚠ Narrowed here for the same reason as the other three, and it is the field
   * whose ABSENCE this boundary has to be able to state: the gateway that is
   * older than this bundle is exactly the gateway that predates
   * `assertDemoIdentityMailSafe`'s boot refusal, so "it answered and did not
   * say" must not be readable as "no mail can be sent".
   */
  readonly notificationDelivery: NotificationDeliveryReport
}

/**
 * ⚠ The ONE cast in this function is over an object whose four dangerous
 * fields have just been CONSTRUCTED rather than cast. The rest of the body is
 * carried through with the same trust `request<T>` already gives it — no
 * more, and no less, than before.
 *
 * A body that is not an object at all (a bare JSON string, `null`, a proxy's
 * stray array) spreads to nothing and every field falls to its absent form,
 * which is the direction that warns (or, for `demonstrationMode`, the
 * direction that withholds the demonstration banner rather than showing it
 * unearned).
 */
async function requestSelfReporting<T extends SelfReport>(path: string, init?: RequestInit): Promise<T> {
  const body = await request<Record<string, unknown> | undefined>(path, init)
  return {
    ...body,
    environment: typeof body?.['environment'] === 'string' ? body['environment'] : null,
    identityProvider: readIdentityProvider(body?.['identityProvider']),
    settlementDispatch: readSettlementDispatch(body?.['settlementDispatch']),
    demonstrationMode: readDemonstrationMode(body?.['demonstrationMode']),
    notificationDelivery: readNotificationDelivery(body?.['notificationDelivery']),
  } as unknown as T
}

/**
 * ★★ WAVE 2b — ONE query builder for the preview and the export.
 *
 * ⚠ The two surfaces MUST send byte-identical field and filter parameters, or
 * the officer previews one population and downloads another. That is a quiet
 * wrong answer of exactly the kind this repository's own working agreements
 * name, so the two callers share this function rather than each assembling a
 * `URLSearchParams`.
 */
function customReportQuery(params: {
  fields: readonly string[]
  billType?: string
  status?: string
  serviceDateFrom?: string
  serviceDateTo?: string
  limit?: number
  offset?: number
  /**
   * ★★ 2026-08-24. A catalogue FIELD ID, resolved server-side against the same
   * catalogue `fields` is — see `custom-report.ts`.
   *
   * ⚠ Set by the PREVIEW only. `customReportUrl` builds the export URL through this
   * same helper and does not pass them, because the export's order is canonical —
   * that route's own comment says why.
   */
  sort?: string
  dir?: string
}): URLSearchParams {
  const query = new URLSearchParams()
  query.set('fields', params.fields.join(','))
  if (params.billType !== undefined && params.billType.length > 0) query.set('billType', params.billType)
  if (params.status !== undefined && params.status.length > 0) query.set('status', params.status)
  if (params.serviceDateFrom !== undefined && params.serviceDateFrom.length > 0) {
    query.set('serviceDateFrom', params.serviceDateFrom)
  }
  if (params.serviceDateTo !== undefined && params.serviceDateTo.length > 0) {
    query.set('serviceDateTo', params.serviceDateTo)
  }
  if (params.limit !== undefined) query.set('limit', String(params.limit))
  if (params.offset !== undefined) query.set('offset', String(params.offset))
  if (params.sort !== undefined) query.set('sort', params.sort)
  if (params.dir !== undefined) query.set('dir', params.dir)
  return query
}

/**
 * ★★★ WAVE 12 (P4) — ONE CODE'S HISTORY FROM ANY ADMINISTERED CATALOGUE, with the
 * URL built in exactly one place.
 *
 * Every `GET /api/administration/<collection>/<key>` route has answered a series
 * since Phase 6, and until Wave 12 **no client called any of them** — the RTM's
 * Annex A 26–28 row: *"the API supports it, the screen shows only the current"*. An
 * endpoint no client calls is indistinguishable from one that does not work.
 *
 * ⚠ `asRecordedAt` is the BI-TEMPORAL rewind, not a date filter: it answers *"what
 * did the system know at this instant"*, so a version recorded later is dropped AND
 * a window closed later is handed back OPEN. Wave 11's S-078 fixed the bound this
 * depends on — twelve repositories were returning an EMPTY history when asked about
 * a version's own recorded instant — so any screen built before that would have
 * rendered a confidently wrong reconstruction of the past.
 *
 * ⚠ **Validates nothing, deliberately.** The value goes up as the control produced
 * it and the ROUTE refuses a malformed instant with a 400 — the same division of
 * labour every other builder here follows. A client that silently dropped an
 * unreadable instant would show the PRESENT while the officer believed they were
 * looking at the past, which is the worst available outcome on this surface.
 *
 * ⚠ `encodeURIComponent` on the key: several of these are operator-entered codes,
 * and one that contained a slash would otherwise address a different route.
 */
function catalogueSeries<T>(
  collection: string,
  key: string,
  asRecordedAt?: string,
  extraKeys: Readonly<Record<string, string>> = {},
): Promise<CatalogueSeriesResponse<T>> {
  const query = new URLSearchParams()
  if (asRecordedAt !== undefined && asRecordedAt.length > 0) query.set('asRecordedAt', asRecordedAt)
  // ⚠ The SECOND key of a composite catalogue, which its series genuinely requires
  // — see `agencyMappingSeries`. Passed as a map rather than a positional argument
  // so the three composite routes' differing parameter NAMES stay at the call site,
  // where a reader can see which route wants which.
  for (const [name, value] of Object.entries(extraKeys)) {
    if (value.length > 0) query.set(name, value)
  }
  const suffix = query.toString()
  return request(
    `/api/administration/${collection}/${encodeURIComponent(key)}${suffix.length > 0 ? `?${suffix}` : ''}`,
  )
}

// ⚠ `compositeSubject` / `splitCompositeSubject` used to live here and MOVED to
// `lib/composite-subject.ts`. They are pure string functions with no I/O, and while
// they sat in this module every DOM test that `vi.mock`ed it also had to stub them —
// eleven went red before the move. `api.ts` is the transport boundary; a module that
// has to be mocked for the wrong reasons is a module with two jobs.

export const api = {
  health: (): Promise<{ status: string }> => request('/health'),

  identities: (): Promise<IdentitiesResponse> => requestSelfReporting('/api/identities'),

  login: (email: string): Promise<SessionResponse> =>
    requestSelfReporting('/api/session', { method: 'POST', body: JSON.stringify({ email }) }),

  /** The demonstration accounts a System Administrator may act as (ADR 0015). 404 when the gateway does not offer it. */
  assumeAccounts: (): Promise<AssumeAccountsResponse> => request('/api/assume/accounts'),

  /** Become one of them: the gateway replaces the session and answers with the new one. */
  assumeUser: (userId: string): Promise<SessionResponse> =>
    requestSelfReporting('/api/session/assume', { method: 'POST', body: JSON.stringify({ userId }) }),

  /** Step back to the administrator's own account. */
  assumeSelf: (): Promise<SessionResponse> => requestSelfReporting('/api/session/assume-self', { method: 'POST', body: JSON.stringify({}) }),

  /**
   * ★★ WAVE 6 — finish a federated sign-in.
   *
   * ⚠ **THE SAME ROUTE the demo roster posts to**, with a different body: the
   * gateway branches on the shape (`identity-port.ts`'s `LoginAttempt`), and
   * `federated-oidc-identity-provider.ts`'s own header records that this is why
   * no new HTTP surface was added for the callback. A second endpoint here would
   * be a second place a session is minted.
   *
   * ⚠ `code` and `state` are read off the browser's own URL and passed through
   * UNTOUCHED. The client validates neither: `state` is looked up server-side
   * against a challenge it minted and refuses an unknown, replayed or expired
   * one, and `code` is only ever meaningful to the token endpoint. Anything this
   * client checked would be a check the server must repeat anyway.
   */
  completeFederatedLogin: (code: string, state: string): Promise<SessionResponse> =>
    requestSelfReporting('/api/session', { method: 'POST', body: JSON.stringify({ code, state }) }),

  logout: (): Promise<void> => request('/api/session', { method: 'DELETE' }),

  me: (): Promise<MeResponse> => requestSelfReporting('/api/me'),

  /**
   * ★ TASK 5D. `limit`/`cursor` are new. `cursor` is OPAQUE — always the exact
   * string a previous response's `nextCursor` carried, never constructed here
   * — and it is only valid for the `sort`/`dir` it was minted under; the
   * gateway refuses a mismatched one with 400 rather than silently
   * reinterpreting it (see `lib/usePager.ts`, which resets on a sort change
   * for exactly this reason).
   */
  bills: (
    params: {
      sort?: string
      dir?: string
      limit?: number
      cursor?: string
      /**
       * ★★★ WAVE 11 — narrow the list SERVER-SIDE, so `total` is the filtered
       * population rather than the whole scope.
       *
       * ⚠ **A cursor is only valid for the filter it was minted under**, exactly as
       * it is only valid for the sort — the gateway refuses a mismatch with 400. Any
       * caller that changes this must reset its paging, which is what
       * `usePager`/`useOffsetPager` already do on a sort change and for the same
       * reason.
       */
      status?: readonly string[]
    } = {},
  ): Promise<BillsListResponse> => {
    const query = new URLSearchParams()
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.cursor !== undefined) query.set('cursor', params.cursor)
    // ⚠ `append`, not `set`: a multi-status filter is a REPEATED key, and `set`
    // would keep only the last one — a filter that silently narrowed to one member
    // while the screen showed several selected.
    for (const status of params.status ?? []) query.append('status', status)
    const suffix = query.toString()
    return request(`/api/bills${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  /**
   * ★★ WAVE 8 (Task 2, S-029) — the whole-scope count of bills by status.
   *
   * ⚠ **No `limit` and no `cursor`, and neither is missing.** The answer is one
   * integer per value `app.bill.status` admits, so it is bounded by the schema
   * rather than by the corpus — which is the entire reason this endpoint exists
   * instead of a bigger page. Anything that computed these figures from
   * {@link api.bills}'s rows would be counting a page and calling it a population.
   */
  billStatusCounts: (): Promise<BillStatusCountsResponse> => request('/api/bills/status-counts'),

  /**
   * ★★★ WAVE 27. `GET /api/bills/series` — the dashboard's trend, counted per day
   * in SQL over the caller's whole scope.
   *
   * ⚠ `days` is a REQUEST, not a guarantee: the route clamps it and the response
   * echoes the value actually used, so a screen prints the period it got rather
   * than the one it asked for.
   */
  /**
   * ★★★ WAVE 27. `POST /api/reports/email` — queue a report for email.
   *
   * ⚠ **THE RESPONSE'S `note` IS THE SERVER'S OWN SENTENCE and callers render it
   * verbatim.** It says what this deployment will actually do with the message,
   * derived from the mail transport the gateway built rather than from an
   * environment name. Composing a friendlier one in the browser would be a second
   * place to decide what a deployment can do — and in every environment this
   * repository ships the honest answer is that nothing will be sent.
   */
  emailReport: (request_: {
    path: string
    format: 'pdf' | 'xlsx'
    to: string
    note?: string
    query?: Readonly<Record<string, string>>
  }): Promise<EmailReportResponse> =>
    request('/api/reports/email', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(request_),
    }),

  billSeries: (days?: number): Promise<DashboardSeriesResponse> =>
    request(`/api/bills/series${days === undefined ? '' : `?days=${days}`}`),

  /**
   * ★★★ WAVE 27. `GET /api/records/alert-counts` — the interface and batch
   * failure counts this scope can see.
   *
   * ⚠ **No `limit` and no `cursor`, for the same reason {@link api.billStatusCounts}
   * has none**: the answer is a handful of integers grouped by a reason column,
   * bounded by the vocabulary rather than by the corpus. Counting rejections from
   * {@link api.records}'s rows would be counting a page and calling it a
   * population — and worse here than for bills, because rejections are a small
   * minority of the records a page returns.
   */
  recordAlertCounts: (): Promise<InterfaceAlertCountsResponse> => request('/api/records/alert-counts'),

  // ⚠ `BillDetailResponse`, not `BillResponse`: this is the ONE bill endpoint that
  // publishes `rateProvenance`. The four writes below answer `BillResponse`, which
  // has no such field, so a screen physically cannot render the disclosure from a
  // write response — see `BillResponse`'s own doc comment.
  bill: (reference: string): Promise<BillDetailResponse> => request(`/api/bills/${encodeURIComponent(reference)}`),

  computeBill: (reference: string): Promise<BillResponse> =>
    request(`/api/bills/${encodeURIComponent(reference)}/compute`, { method: 'POST' }),

  /**
   * ★★ WAVE 9 — Annex A 37/38's approval COMMENT, optional on all three decisions.
   *
   * ⚠ An ABSENT comment sends NO `comment` key rather than `comment: ''`. The
   * gateway treats a blank one as absent and the database refuses a zero-length
   * value outright (`BETWEEN 1 AND 1000`), so sending an empty string would be
   * asking for a 400 the client could have avoided — and it would put the question
   * "did the officer leave a remark?" in two places with two answers.
   */
  approveBill: (reference: string, comment?: string): Promise<BillResponse> =>
    request(`/api/bills/${encodeURIComponent(reference)}/approve`, {
      method: 'POST',
      body: JSON.stringify(comment === undefined || comment.length === 0 ? {} : { comment }),
    }),

  approveLevel2Bill: (reference: string, comment?: string): Promise<BillResponse> =>
    request(`/api/bills/${encodeURIComponent(reference)}/approve-l2`, {
      method: 'POST',
      body: JSON.stringify(comment === undefined || comment.length === 0 ? {} : { comment }),
    }),

  rejectBill: (reference: string, reason: string, comment?: string): Promise<BillResponse> =>
    request(`/api/bills/${encodeURIComponent(reference)}/reject`, {
      method: 'POST',
      body: JSON.stringify(comment === undefined || comment.length === 0 ? { reason } : { reason, comment }),
    }),

  /**
   * ★★ WAVE 10 — pause a bill pending information from the HSP (Annex A 37/38).
   *
   * ⚠ `comment` is REQUIRED here where it is optional on an approval, and it is
   * sent unconditionally rather than omitted when blank. The three approval calls
   * above drop an empty comment so the server sees "no comment"; here an empty
   * comment must reach the server and be REFUSED, because a pause with no
   * statement of what is needed is the send-back trap. Silently omitting it would
   * turn a 400 the officer can act on into a body the schema accepts.
   */
  requestBillInformation: (reference: string, comment: string): Promise<BillResponse> =>
    request(`/api/bills/${encodeURIComponent(reference)}/request-information`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    }),

  /** ★★★ WAVE 10 — THE RETURN PATH. The destination comes off the ledger, never from here. */
  resumeBill: (reference: string, comment: string): Promise<BillResponse> =>
    request(`/api/bills/${encodeURIComponent(reference)}/resume`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    }),

  /**
   * ★★ WAVE 10 — refer a bill to a named colleague.
   *
   * ⚠ `referredToUserId` is an ID. There is no name-taking variant of this call and
   * there must never be one: two colleagues can share a display name, and
   * resolving one client-side would file the referral against whichever sorted
   * first while rendering the right name. Get the id from
   * {@link referralCandidates}.
   */
  referBill: (reference: string, referredToUserId: string, comment: string): Promise<ReferBillResponse> =>
    request(`/api/bills/${encodeURIComponent(reference)}/refer`, {
      method: 'POST',
      body: JSON.stringify({ referredToUserId, comment }),
    }),

  /** ★★ WAVE 10 — the colleagues who can actually be asked about THIS bill. */
  referralCandidates: (reference: string): Promise<ReferralCandidatesResponse> =>
    request(`/api/bills/${encodeURIComponent(reference)}/referral-candidates`),

  /** ★★ WAVE 10 — what colleagues have asked ME to look at. */
  referralsToMe: (): Promise<ReferralInboxResponse> => request('/api/bills/referred-to-me'),

  /**
   * A bill's approval-lifecycle ledger — the ONLY place the level 2 approver's
   * identity and a rejection's reason are readable.
   *
   * ⚠ 404 covers both "no such bill" and "not in your scope", with a
   * byte-identical body, exactly as `GET /api/bills/:reference` does. A caller
   * must not try to tell them apart: the indistinguishability is the design,
   * and this endpoint would be an unusually good enumeration oracle otherwise —
   * it reports who approved what.
   */
  billTransitions: (reference: string): Promise<BillTransitionsResponse> =>
    request(`/api/bills/${encodeURIComponent(reference)}/transitions`),

  /** ★ PHASE 10 (Task 36) — Annex A 3/4. The full adjustment chain, read-only: adjustments arrive by file, never from this screen. */
  /**
   * ★★★ WAVE 13 (P7) - one bill's re-proration history (P2C1 §12, RA row 7).
   *
   * ⚠ A READ. The sweep that CREATES these rows is
   * `POST /api/bills/reprorate-sweep`, which is an estate-wide operator action
   * and is deliberately not reachable from a bill's own screen: re-dividing one
   * bill because its officer's entitlement moved, while leaving every other bill
   * that officer holds untouched, is not a coherent operation.
   */
  billReprorations: (reference: string): Promise<BillReprorationsResponse> =>
    request(`/api/bills/${encodeURIComponent(reference)}/reprorations`),

  /**
   * ★★★ WAVE 14 — the sweep itself, from a screen.
   *
   * ⚠ **This MOVES MONEY between the public purse and officers.** The comment
   * three functions up says the sweep is "deliberately not reachable from a
   * bill's own screen", and that is still true and is not what changed: it is
   * reachable from ONE estate-wide operator console, which is the shape the
   * operation actually has. Re-dividing one bill because its officer's
   * entitlement moved, while leaving that officer's other bills alone, is still
   * not an operation this product offers.
   *
   * ⚠ `dryRun` decides and reports and writes NOTHING — no ledger row, no
   * audit event, no changed split. The console defaults to it.
   */
  runReprorationSweep: (
    params: { dryRun?: boolean; limit?: number; afterReference?: string } = {},
  ): Promise<ReprorationSweepResponse> => {
    const query = new URLSearchParams()
    if (params.dryRun === true) query.set('dryRun', 'true')
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    // ⚠ Sent only when non-empty. An empty `afterReference` is not a cursor and
    // the route says so; sending one would be a caller asserting a position it
    // does not have.
    if (params.afterReference !== undefined && params.afterReference.length > 0) {
      query.set('afterReference', params.afterReference)
    }
    const suffix = query.toString()
    return request(`/api/bills/reprorate-sweep${suffix.length > 0 ? `?${suffix}` : ''}`, { method: 'POST' })
  },

  /**
   * ★★★ WAVE 14 — the Recovery Agency alert file (P2C1 §12, RA row 7).
   *
   * ⚠ One file per Recovery Agency per business date is enforced by the
   * DATABASE, so a second call on the same day is refused rather than producing
   * a second file. The console renders that refusal as a boundary rather than as
   * an error, because it is the product working.
   */
  generateReprorationAlert: (counterpartyCode: string): Promise<ReprorationAlertResponse> =>
    request(`/api/reproration-alerts/${encodeURIComponent(counterpartyCode)}`, { method: 'POST' }),

  billAdjustments: (reference: string): Promise<BillAdjustmentsResponse> =>
    request(`/api/bills/${encodeURIComponent(reference)}/adjustments`),

  /** ★ PHASE 10 (Task 38) — Annex A 7. The payment ledger and THEIR settlement axis, read-only. */
  billPayments: (reference: string): Promise<BillPaymentsResponse> =>
    request(`/api/bills/${encodeURIComponent(reference)}/payments`),

  entitlement: (officerId: string, serviceDate: string): Promise<EntitlementResponse> =>
    request(
      `/api/entitlement?officerId=${encodeURIComponent(officerId)}&serviceDate=${encodeURIComponent(serviceDate)}`,
    ),

  audit: (
    params: {
      limit?: number
      offset?: number
      resourceType?: string
      resourceId?: string
      action?: string
      sort?: string
      dir?: string
      occurredFrom?: string
      occurredTo?: string
      /**
       * ★★★ S-075 — THE PAGING PIN. The `asOf` the FIRST page published, passed
       * back on every page after it so the whole walk reads one closed
       * population.
       *
       * ⚠ **Not `occurredTo`, and this used to BE `occurredTo`.** That parameter
       * is a date filter and is inclusive of the whole millisecond it names —
       * `occurred_at` keeps microseconds, an instant names milliseconds — which
       * is correct for a filter and one millisecond too wide for an anchor. A pin
       * that wide readmits rows committing after the anchor was read, and the
       * walk can skip again. `?asOf=` is applied exactly.
       */
      asOf?: string
      /** ★★ WAVE 9 — story 15's user-account limb, on the screen at last. */
      actorUserId?: string
      /** ★★ WAVE 9 — story 15's severity limb. Refused by the route if not HIGH/MEDIUM/LOW. */
      severity?: string
    } = {},
  ): Promise<AuditListResponse> => {
    const query = new URLSearchParams()
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.offset !== undefined) query.set('offset', String(params.offset))
    if (params.resourceType !== undefined) query.set('resourceType', params.resourceType)
    if (params.resourceId !== undefined) query.set('resourceId', params.resourceId)
    if (params.action !== undefined) query.set('action', params.action)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.occurredFrom !== undefined) query.set('occurredFrom', params.occurredFrom)
    if (params.occurredTo !== undefined) query.set('occurredTo', params.occurredTo)
    if (params.asOf !== undefined) query.set('asOf', params.asOf)
    if (params.actorUserId !== undefined) query.set('actorUserId', params.actorUserId)
    if (params.severity !== undefined) query.set('severity', params.severity)
    const suffix = query.toString()
    return request(`/api/audit${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  /**
   * ★ TASK 15 — a URL, not a fetch. `GET /api/audit/export` answers with an
   * `attachment` `Content-Disposition`, so a plain navigation (an `<a href>`,
   * as `AuditPage.tsx` uses this) already triggers the browser's own download
   * prompt — no blob handling needed here.
   *
   * ★★ WAVE 9 — IT NOW CARRIES THE SCREEN'S FILTERS TOO, AND THAT IS THE POINT
   * OF THE CHANGE RATHER THAN A SIDE EFFECT. This docstring read: *"Carries the
   * screen's current sort order and nothing else: the export's own
   * date-range/user-account/severity filters have no UI control on this screen
   * yet… an authorised caller who needs them constructs the query directly
   * against the API."* The controls exist now, and an export that ignored them
   * would be the worse half of the old state — a **Download** button beside three
   * populated filter fields, producing a file of the whole trail. Story 15's own
   * words are *"export exactly what is on it"*, and a download that silently
   * widens its own scope is the shape this repository records as its worst
   * failure mode.
   *
   * ⚠ The parameter names are the ROUTE's, identical on both surfaces
   * (`occurredFrom`, `occurredTo`, `actorUserId`, `severity`), so the two cannot
   * drift into meaning different things. The export route validates them itself
   * — this function is a URL builder and validates nothing, deliberately.
   */
  auditExportUrl: (
    format: 'pdf' | 'xlsx',
    state: {
      sort?: string
      dir?: string
      occurredFrom?: string
      occurredTo?: string
      actorUserId?: string
      severity?: string
    } = {},
  ): string => {
    const query = new URLSearchParams({ format })
    if (state.sort !== undefined) query.set('sort', state.sort)
    if (state.dir !== undefined) query.set('dir', state.dir)
    if (state.occurredFrom !== undefined) query.set('occurredFrom', state.occurredFrom)
    if (state.occurredTo !== undefined) query.set('occurredTo', state.occurredTo)
    if (state.actorUserId !== undefined) query.set('actorUserId', state.actorUserId)
    if (state.severity !== undefined) query.set('severity', state.severity)
    return `/api/audit/export?${query.toString()}`
  },

  /**
   * ★★ WAVE 9 — the audit ARCHIVE operator surfaces, P2C1 §12.134 and §12.135.
   *
   * ⚠ All four gate on `mayReadFullAuditTrail` at the ROUTE and answer 403 to
   * anything else, which is why the navigation link is gated too — the rule
   * `Shell.tsx` states is that a link is gated only where the route answers 403,
   * and here it does.
   */
  /** ★★ `sort`/`dir` added 2026-08-24 — this table had eight plain headers. */
  archiveSegments: (
    params: { sort?: string; dir?: string; offset?: number } = {},
  ): Promise<ArchiveSegmentsResponse> => {
    const query = new URLSearchParams()
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    // ★★ 2026-08-24 (Wave 18) — the walk this list did not have.
    if (params.offset !== undefined && params.offset > 0) query.set('offset', String(params.offset))
    const suffix = query.toString()
    return request(`/api/audit/archive${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  archiveStatus: (): Promise<ArchiveStatusResponse> => request('/api/audit/archive/status'),

  archiveVerify: (segmentId: string): Promise<ArchiveVerifyResponse> =>
    request(`/api/audit/archive/${encodeURIComponent(segmentId)}/verify`),

  /**
   * ⚠ A WRITE, and the only one on that screen. It seals a segment over a bounded
   * backlog and is an OPERATOR action with no scheduler behind it (ADR 0009
   * Decision 4) — the screen says so in every state rather than letting a button
   * imply an operational rhythm the deployment does not have.
   */
  runArchiveRotation: (): Promise<ArchiveRunResponse> => request('/api/audit/archive/run', { method: 'POST' }),

  /**
   * ★★★ WAVE 13 (P9) — P2C1 §12.135's alerting limb, pushed rather than polled.
   *
   * Takes the SAME reading `archiveStatus` takes and raises
   * `AUDIT_STORAGE_THRESHOLD_REACHED` when the level is not OK.
   *
   * ⚠ **A WRITE, which is why it is not folded into `archiveStatus`.** A GET that
   * raised would alert if and only if somebody happened to open the page — the
   * exact failure this increment closes. And like the rotation it is an OPERATOR
   * action with no scheduler behind it; `scripts/run-storage-alert-sweep.mjs` is
   * the same call from a terminal, for a deployment that has one.
   */
  sweepArchiveAlerts: (): Promise<ArchiveAlertSweepResponse> =>
    request('/api/audit/archive/alerts/sweep', { method: 'POST' }),

  // ─────────────────────────────────────────────────────────────────────
  // ★★★ WAVE 13 (P10) — P2C1 §12.141's retention register.
  // ─────────────────────────────────────────────────────────────────────

  retention: (): Promise<RetentionResponse> => request('/api/audit/retention'),

  retentionSeries: (subject: string): Promise<{ subject: string; entries: readonly RetentionPolicy[] }> =>
    request(`/api/audit/retention/${encodeURIComponent(subject)}`),

  /**
   * ⚠ A WRITE onto a COMPLIANCE REGISTER. It records what the Authority requires;
   * it causes nothing to happen to any record. A DISPOSE_AT_EXPIRY policy is
   * recorded and never performed, and the route says so in its own response.
   */
  supersedeRetentionPolicy: (
    subject: string,
    body: {
      effectiveFrom: string
      establish?: boolean
      disposition?: string
      retentionMonths?: number | null
      standardReference?: string
      note?: string
    },
  ): Promise<unknown> =>
    request(`/api/audit/retention/${encodeURIComponent(subject)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * ★★ WAVE 9 — P2C1 §12.52's download half for the bill enquiry (Annex A 9/10).
   *
   * A URL, not a fetch, for the reason `auditExportUrl` gives: the route answers
   * with an `attachment` Content-Disposition, so a plain navigation triggers the
   * browser's own download and the officer stays on the page.
   *
   * ⚠ **It carries the screen's own filter and sort and NOT its cursor.** The route
   * accepts no cursor at all: an export is one document over one filter, and a file
   * whose contents depended on which page the officer happened to be on while its
   * title claimed to be the enquiry is the widening-under-a-narrow-label defect this
   * product records as its worst. The document states its own bound and refuses past
   * it rather than truncating.
   */
  recordsExportUrl: (
    format: 'pdf' | 'xlsx',
    state: { sort?: string; dir?: string; outcome?: string } = {},
  ): string => {
    const query = new URLSearchParams({ format })
    if (state.sort !== undefined) query.set('sort', state.sort)
    if (state.dir !== undefined) query.set('dir', state.dir)
    if (state.outcome !== undefined) query.set('outcome', state.outcome)
    return `/api/records/export?${query.toString()}`
  },

  /**
   * ★★ WAVE 9 — the same, for the point-of-contact directory (Annex A 11/12).
   *
   * ⚠ Carries the filters and the sort, never the OFFSET — same argument as above,
   * and the route accepts no offset either.
   */
  contactsExportUrl: (
    format: 'pdf' | 'xlsx',
    state: { sort?: string; dir?: string; kind?: string; contactRole?: string } = {},
  ): string => {
    const query = new URLSearchParams({ format })
    if (state.sort !== undefined) query.set('sort', state.sort)
    if (state.dir !== undefined) query.set('dir', state.dir)
    if (state.kind !== undefined) query.set('kind', state.kind)
    if (state.contactRole !== undefined) query.set('contactRole', state.contactRole)
    return `/api/contacts/export?${query.toString()}`
  },

  /**
   * ★ TASK 16 — Annex A story 16. Same shape as `auditExportUrl` above: a
   * plain navigation URL, never a fetch, because the route answers with an
   * `attachment` Content-Disposition. Carries no filters yet — the
   * date-range/user-account filters this route also accepts have no UI
   * control here, the same narrowing `auditExportUrl`'s own filters carry
   * (see `docs/requirements/RTM.md`'s Annex A 16–17 row).
   */
  authenticationReportUrl: (format: 'pdf' | 'xlsx'): string => `/api/reports/authentication?format=${format}`,

  /**
   * ★★ WAVE 9 — Annex A story 37's per-officer approval-activity report.
   *
   * ⚠ A plain navigation like the others, and it answers **403** to a role without
   * the full-audit-trail capability — surfaced as a failed download rather than
   * fetched and checked, matching every export link on that page. The refusal is not
   * a narrowing: the officer IS the grouping key, so there is no smaller version of
   * this report to serve.
   */
  approvalActivityReportUrl: (format: 'pdf' | 'xlsx'): string =>
    `/api/reports/approval-activity?format=${format}`,

  /**
   * ★ TASK 16 — Annex A story 17. A role that does not hold the full-trail
   * capability gets a 403 JSON body rather than a file — this is a plain
   * navigation like the others, so that refusal surfaces as the browser's
   * own failed-download behaviour rather than a caught fetch error, exactly
   * like every other export link on this page.
   */
  accessControlMatrixUrl: (format: 'pdf' | 'xlsx', axis: 'capability' | 'user' | 'findings' = 'capability'): string =>
    `/api/reports/access-control-matrix?format=${format}&axis=${axis}`,

  /**
   * ★★ Annex A story 17 — the whole matrix in one read: every capability by role,
   * every account's authority, and the findings.
   *
   * ⚠ ONE call rather than three, because the three axes must describe ONE moment.
   * A screen that fetched findings and accounts separately could render a finding
   * about an account the account list no longer shows.
   *
   * ⚠ `authMethod` and `department` are NOT parameters here. The gateway REFUSES
   * both with a reason (they are unanswerable in this system), and the reasons
   * arrive on the response as `filtersNotAvailable` so the screen can say so
   * without a second copy of the explanation.
   */
  accessControlMatrix: (
    filter: {
      role?: string
      agencyCode?: string
      /**
       * ★★ The two pagers, 2026-08-20. The user axis and the findings axis page
       * INDEPENDENTLY — see `MatrixPaging`. Omitted means page one of that axis, and
       * the gateway's own clamp decides the page size, which this file never states.
       */
      offset?: number
      sort?: string
      dir?: 'asc' | 'desc'
      findingOffset?: number
      findingSort?: string
      findingDir?: 'asc' | 'desc'
    } = {},
  ): Promise<AccessControlMatrixResponse> => {
    const query = new URLSearchParams()
    if (filter.role !== undefined && filter.role.length > 0) query.set('role', filter.role)
    if (filter.agencyCode !== undefined && filter.agencyCode.length > 0) query.set('agencyCode', filter.agencyCode)
    // ⚠ Sent only when non-zero / present: `?offset=0` is the default the gateway
    // already applies, and an empty `?sort=` would be a column name of "".
    if (filter.offset !== undefined && filter.offset > 0) query.set('offset', String(filter.offset))
    if (filter.sort !== undefined && filter.sort.length > 0) {
      query.set('sort', filter.sort)
      query.set('dir', filter.dir ?? 'asc')
    }
    if (filter.findingOffset !== undefined && filter.findingOffset > 0) {
      query.set('findingOffset', String(filter.findingOffset))
    }
    if (filter.findingSort !== undefined && filter.findingSort.length > 0) {
      query.set('findingSort', filter.findingSort)
      query.set('findingDir', filter.findingDir ?? 'asc')
    }
    const suffix = query.toString()
    return request<AccessControlMatrixResponse>(`/api/access-control-matrix${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  /**
   * ★★ WAVE 2b — Annex A story 13. The report field CATALOGUE.
   *
   * ⚠ A fetch, not a constant. See `CustomReportFieldsResponse`'s own doc: a
   * client-side copy of the field list drifts silently the first time a field
   * is added at the gateway.
   */
  customReportFields: (): Promise<CustomReportFieldsResponse> => request('/api/reports/custom/fields'),

  /**
   * ★★ WAVE 2b — the on-screen preview of the SAME query the export renders,
   * paged by `?offset=` over a total order (`b.reference ASC`).
   *
   * ⚠ `fields` is sent as the caller chose them, in order, and the SERVER is
   * the only thing that decides whether an id is selectable — this function
   * validates nothing, deliberately, so there is exactly one catalogue lookup
   * in the product and it is the one behind the injection defence.
   */
  customReportPreview: (params: {
    fields: readonly string[]
    billType?: string
    status?: string
    serviceDateFrom?: string
    serviceDateTo?: string
    limit?: number
    offset?: number
    sort?: string
    dir?: string
  }): Promise<CustomReportPreviewResponse> =>
    request(`/api/reports/custom/preview?${customReportQuery(params).toString()}`),

  /**
   * A plain navigation URL, exactly like every other export on this app's
   * screens — `GET /api/reports/custom` answers with an `attachment`
   * Content-Disposition, so the browser's own download handles it and a refusal
   * (400 for a bad field, 422 for a too-wide PDF or an over-bound population)
   * surfaces as the browser's own failed-download behaviour.
   */
  customReportUrl: (
    format: 'pdf' | 'xlsx',
    params: {
      fields: readonly string[]
      billType?: string
      status?: string
      serviceDateFrom?: string
      serviceDateTo?: string
    },
  ): string => {
    const query = customReportQuery(params)
    query.set('format', format)
    return `/api/reports/custom?${query.toString()}`
  },

  /**
   * ★ TASK 11. `GET /api/records` — Annex A stories 9 and 10. Same keyset
   * `cursor`/`limit` contract as `api.bills()`: `cursor` is always the exact
   * string a previous response's `nextCursor` carried, never constructed
   * here, and it is only valid for the `sort`/`dir` it was minted under.
   */
  records: (
    params: {
      outcome?: 'ACCEPTED' | 'REJECTED'
      reasonKind?: string
      sourceReference?: string
      receivedFrom?: string
      receivedTo?: string
      sort?: string
      dir?: string
      limit?: number
      cursor?: string
    } = {},
  ): Promise<RecordsListResponse> => {
    const query = new URLSearchParams()
    if (params.outcome !== undefined) query.set('outcome', params.outcome)
    if (params.reasonKind !== undefined) query.set('reasonKind', params.reasonKind)
    if (params.sourceReference !== undefined) query.set('sourceReference', params.sourceReference)
    if (params.receivedFrom !== undefined) query.set('receivedFrom', params.receivedFrom)
    if (params.receivedTo !== undefined) query.set('receivedTo', params.receivedTo)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.cursor !== undefined) query.set('cursor', params.cursor)
    const suffix = query.toString()
    return request(`/api/records${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  /** ★★ WAVE 1a. `GET /api/inbound/channels` — what this caller may upload onto, and the bounds. */
  inboundChannels: (): Promise<InboundChannelsResponse> => request('/api/inbound/channels'),

  /**
   * ★★ WAVE 1a. `POST /api/inbound/channels/:channelId/files` — Annex A 2-4.
   *
   * ⚠ **The `File` IS the body, not a `FormData` field.** The gateway takes
   * `application/octet-stream` and runs no parser at all: a multipart parser
   * would be a new dependency and a well-populated CVE class on the one route
   * whose whole job is to accept a hostile party's bytes. `fetch` sends a
   * `File` as its own bytes, so what leaves the browser is exactly what reaches
   * the content digest.
   *
   * ⚠ **The name travels in a HEADER, not the URL.** It is chosen by whoever is
   * uploading, and a request LINE ends up in more logs than a header does.
   */
  uploadInboundFile: (channelId: string, file: File): Promise<InboundUploadResponse> =>
    request(`/api/inbound/channels/${encodeURIComponent(channelId)}/files`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/octet-stream',
        'x-prs2-file-name': asciiHeaderValue(file.name),
      },
      body: file,
    }),

  /**
   * ★★ WAVE 1c. `POST /api/inbound/channels/:channelId/adjustments` — Annex A
   * stories 3 and 4.
   *
   * ⚠ **This composes a DELIVERY, it does not write an adjustment.** The server
   * renders these fields into a one-record `PRS2-HSP-BILL` file, deposits it on
   * the channel and reads it back with the same pipeline a delivered file goes
   * through — identical grammar, identical authorisation, identical caps. A
   * form that wrote the effect directly would be a second path to the money,
   * which is exactly what S-056 cost.
   *
   * ⚠ The INDICATOR is not sent: it is the sign of `deltaCents`, derived on the
   * server, so a caller cannot give the two contradicting values.
   */
  composeInboundAdjustment: (
    channelId: string,
    body: { sourceReference: string; originalReference: string; deltaCents: number; serviceDate?: string },
  ): Promise<InboundAdjustmentResponse> =>
    request(`/api/inbound/channels/${encodeURIComponent(channelId)}/adjustments`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /** ★ TASK 12. `GET /api/contacts` — Annex A stories 11 and 12. */
  contacts: (
    params: {
      counterpartyCode?: string
      kind?: 'HSP' | 'PAYMENT' | 'RECOVERY' | 'ENTITLEMENT'
      /** ★★ 2026-08-18 — the page offset; see list-reachability.test.ts. */
      offset?: number
      contactRole?: 'BILLING' | 'TECHNICAL' | 'MANAGEMENT'
      sort?: string
      dir?: string
      limit?: number
    } = {},
  ): Promise<ContactsListResponse> => {
    const query = new URLSearchParams()
    // ★★ 2026-08-18 — the page offset; see services/gateway/src/list-reachability.test.ts.
    if (params.offset !== undefined && params.offset > 0) query.set('offset', String(params.offset))
    if (params.counterpartyCode !== undefined) query.set('counterpartyCode', params.counterpartyCode)
    if (params.kind !== undefined) query.set('kind', params.kind)
    if (params.contactRole !== undefined) query.set('contactRole', params.contactRole)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const suffix = query.toString()
    return request(`/api/contacts${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 6, TASK 18 — Annex A story 26, `app.ward_class`.
  // ─────────────────────────────────────────────────────────────────────

  wardClasses: (params: { category?: string; sort?: string; dir?: string; limit?: number } = {}): Promise<WardClassListResponse> => {
    const query = new URLSearchParams()
    if (params.category !== undefined) query.set('category', params.category)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const suffix = query.toString()
    return request(`/api/administration/ward-classes${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  /** A plain navigation URL — `GET .../export` answers with an `attachment` Content-Disposition. */
  wardClassExportUrl: (format: 'pdf' | 'xlsx'): string => `/api/administration/ward-classes/export?format=${format}`,

  // ─────────────────────────────────────────────────────────────────────
  // ★★★ WAVE 13 (P8) — Annex A 19-21, `app.ward_rule_version`. The MONEY
  // sibling of the ward-class catalogue above; see routes/ward-rules.ts.
  // ─────────────────────────────────────────────────────────────────────

  wardRules: (params: { scheme?: string; sort?: string; dir?: string; limit?: number } = {}): Promise<WardRuleListResponse> => {
    const query = new URLSearchParams()
    if (params.scheme !== undefined) query.set('scheme', params.scheme)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const suffix = query.toString()
    return request(`/api/reference/ward-rules${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  wardRuleExportUrl: (format: 'pdf' | 'xlsx'): string => `/api/reference/ward-rules/export?format=${format}`,

  /**
   * One rule key's whole history, oldest version first.
   *
   * ⚠ No `asRecordedAt` rewind, unlike every other catalogue series — the route
   * does not offer one, deliberately. `app.bill` stores both `applied_rule_id`
   * and `applied_rule_version`, so "what priced this bill" is answered exactly by
   * the bill's own pair rather than by reconstructing the register at an instant.
   */
  wardRuleSeries: (id: string): Promise<{ id: string; entries: readonly WardRule[] }> =>
    request(`/api/reference/ward-rules/${encodeURIComponent(id)}`),

  /**
   * ⚠ A WRITE THAT MOVES MONEY. The rate authored here prices every future
   * CPW/FAW/CWM bill for its ward and service-date window.
   *
   * ⚠ `note` is REQUIRED whenever `establish` is not false, and the route refuses
   * below 20 non-blank characters — as does the database
   * (`ward_rule_version_authored_note`). It is printed verbatim beside every
   * figure the rate produces.
   */
  supersedeWardRule: (
    id: string,
    body: {
      effectiveFrom: string
      note?: string
      establish?: boolean
      subsidyBasisPoints?: number
    },
  ): Promise<unknown> =>
    request(`/api/reference/ward-rules/${encodeURIComponent(id)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * ★★★ WAVE 12 (P4) — ONE CODE'S WHOLE HISTORY, optionally rewound.
   *
   * ⚠ **The route has answered this since Phase 6 and nothing ever called it.** The
   * RTM's Annex A 26–28 row said so — *"the API supports it, the screen shows only
   * the current"* — and `WardClassesPage`'s own header repeated it about itself. An
   * endpoint no client calls is indistinguishable from one that does not work, which
   * is why this is a gap and not a nicety.
   *
   * ⚠ `asRecordedAt` is the BI-TEMPORAL rewind, not a date filter: it answers *"what
   * did the system know at this instant"*, so a version recorded later is dropped AND
   * a window closed later is handed back OPEN. Wave 11 (S-078) fixed the bound this
   * depends on — twelve repositories were returning an **empty history** when asked
   * about a version's own recorded instant — so a screen built before that would have
   * rendered a confidently wrong answer.
   *
   * ⚠ Sent as the raw value the control produced; the ROUTE validates it (400 on a
   * malformed instant) and this function validates nothing, deliberately — the same
   * division of labour every other builder here follows.
   */
  wardClassSeries: (code: string, asRecordedAt?: string): Promise<CatalogueSeriesResponse<WardClass>> =>
    catalogueSeries('ward-classes', code, asRecordedAt),

  /**
   * ★★★ WAVE 12 (P4) — the other administered catalogues' histories, one thin
   * typed call each over {@link catalogueSeries}.
   *
   * ⚠ **Thin wrappers rather than one untyped `series(collection, key)` call, and
   * rather than nine hand-rolled URL builders.** The URL shape lives once (so a
   * change to `?asRecordedAt=`'s name is one edit, not nine) while the ROW TYPE
   * stays explicit per catalogue (so a screen cannot render an announcement's
   * fields over a scheme code's data and typecheck). Both halves matter: nine
   * builders is the S-011 drift shape, and one untyped call throws the types away
   * at exactly the boundary they are worth having.
   */
  /**
   * ★★★ WAVE 12 (P2) — Annex A story 14's configuration module.
   *
   * ⚠ Never carries a secret: the catalogue holds no client secret at all, so
   * there is nothing here to redact. See `061_identity_provider_config.sql`.
   */
  identityProviders: (): Promise<IdentityProviderListResponse> =>
    request('/api/administration/identity-providers'),

  identityProviderSeries: (
    providerKey: string,
    asRecordedAt?: string,
  ): Promise<CatalogueSeriesResponse<IdentityProvider>> =>
    catalogueSeries('identity-providers', providerKey, asRecordedAt),

  supersedeIdentityProvider: (
    providerKey: string,
    body: {
      effectiveFrom: string
      note?: string
      values?: {
        displayName: string
        enabled: boolean
        requiredAssuranceLevel?: string | null
        displayOrder?: number
      }
    },
  ): Promise<unknown> =>
    request(`/api/administration/identity-providers/${encodeURIComponent(providerKey)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  subsystemSeries: (code: string, asRecordedAt?: string): Promise<CatalogueSeriesResponse<SubsystemInfo>> =>
    catalogueSeries('subsystems', code, asRecordedAt),

  medicalSchemeCodeSeries: (
    code: string,
    asRecordedAt?: string,
  ): Promise<CatalogueSeriesResponse<MedicalSchemeCode>> => catalogueSeries('medical-scheme-codes', code, asRecordedAt),

  hspGroupSeries: (code: string, asRecordedAt?: string): Promise<CatalogueSeriesResponse<HspGroup>> =>
    catalogueSeries('hsp-groups', code, asRecordedAt),

  emailTemplateSeries: (code: string, asRecordedAt?: string): Promise<CatalogueSeriesResponse<EmailTemplate>> =>
    catalogueSeries('email-templates', code, asRecordedAt),

  announcementSeries: (code: string, asRecordedAt?: string): Promise<CatalogueSeriesResponse<Announcement>> =>
    catalogueSeries('announcements', code, asRecordedAt),

  specialAgencyCaseSeries: (
    code: string,
    asRecordedAt?: string,
  ): Promise<CatalogueSeriesResponse<SpecialAgencyCase>> =>
    catalogueSeries('special-agency-cases', code, asRecordedAt),

  /**
   * ★★★ WAVE 12 (P4) — THE THREE COMPOSITE-KEY CATALOGUES.
   *
   * ⚠ **Their series genuinely needs BOTH keys, and that is not an accident of the
   * route.** `GET /api/administration/agency-mappings/:mappingAgencyCode` WITHOUT
   * `?providerAgencyCode=` returns *the agency's current board* — a completely
   * different answer from *one pair's history*. Calling it with one key and
   * rendering the result under a "Version history" heading would be the "answered
   * something adjacent" failure this codebase refuses by name, and it would look
   * entirely plausible. So the second key is required here rather than optional.
   *
   * ⚠ The screens address these through a COMPOUND subject (`'A / B'`) so the one
   * picker in `VersionHistory` still serves all thirteen catalogues;
   * `splitCompositeSubject` is the single place that convention is read back,
   * rather than three page-local splits that could disagree about the separator.
   */
  agencyMappingSeries: (
    mappingAgencyCode: string,
    providerAgencyCode: string,
    asRecordedAt?: string,
  ): Promise<CatalogueSeriesResponse<AgencyMapping>> =>
    catalogueSeries('agency-mappings', mappingAgencyCode, asRecordedAt, { providerAgencyCode }),

  hspGroupMembershipSeries: (
    hspAgencyCode: string,
    groupCode: string,
    asRecordedAt?: string,
  ): Promise<CatalogueSeriesResponse<HspGroupMembership>> =>
    catalogueSeries('hsp-group-memberships', hspAgencyCode, asRecordedAt, { groupCode }),

  mbimRecordSeries: (
    officerId: string,
    entitlementType: string,
    asRecordedAt?: string,
  ): Promise<CatalogueSeriesResponse<MbimRecord>> =>
    catalogueSeries('mbim-records', officerId, asRecordedAt, { entitlementType }),

  supersedeWardClass: (
    code: string,
    body: {
      effectiveFrom: string
      note?: string
      establish?: boolean
      label?: string
      category?: string
      rankOrder?: number
      description?: string | null
    },
  ): Promise<unknown> =>
    request(`/api/administration/ward-classes/${encodeURIComponent(code)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 6, TASK 18 — Annex A story 27, `app.subsystem_info`.
  // ─────────────────────────────────────────────────────────────────────

  // ────────────────────────────────────────────────
  // User account administration (System Administrator only).
  // ────────────────────────────────────────────────

  userRoles: (): Promise<UserRolesResponse> => request('/api/administration/user-roles'),

  userAccounts: (
    params: { search?: string; role?: string; agency?: string; status?: 'active' | 'inactive'; limit?: number; offset?: number } = {},
  ): Promise<UserAccountListResponse> => {
    const query = new URLSearchParams()
    if (params.search !== undefined && params.search.length > 0) query.set('search', params.search)
    if (params.role !== undefined) query.set('role', params.role)
    if (params.agency !== undefined) query.set('agency', params.agency)
    if (params.status !== undefined) query.set('status', params.status)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.offset !== undefined) query.set('offset', String(params.offset))
    const suffix = query.toString()
    return request(`/api/administration/users${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  userAccount: (userId: string): Promise<{ user: UserAccountDetail; changes: readonly AccountChange[] }> =>
    request(`/api/administration/users/${encodeURIComponent(userId)}`),

  createUserAccount: (body: UserAccountBody): Promise<{ user: UserAccountDetail }> =>
    request('/api/administration/users', { method: 'POST', body: JSON.stringify(body) }),

  updateUserAccount: (userId: string, body: UserAccountBody): Promise<{ user: UserAccountDetail }> =>
    request(`/api/administration/users/${encodeURIComponent(userId)}`, { method: 'PUT', body: JSON.stringify(body) }),

  setUserAccountActive: (userId: string, active: boolean, note: string): Promise<{ user: UserAccount }> =>
    request(`/api/administration/users/${encodeURIComponent(userId)}/${active ? 'reactivate' : 'deactivate'}`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  subsystems: (
    params: { status?: SubsystemStatusValue; sort?: string; dir?: string; limit?: number } = {},
  ): Promise<SubsystemListResponse> => {
    const query = new URLSearchParams()
    if (params.status !== undefined) query.set('status', params.status)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const suffix = query.toString()
    return request(`/api/administration/subsystems${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  subsystemExportUrl: (format: 'pdf' | 'xlsx'): string => `/api/administration/subsystems/export?format=${format}`,

  supersedeSubsystem: (
    subsystemCode: string,
    body: {
      effectiveFrom: string
      note?: string
      establish?: boolean
      name?: string
      description?: string | null
      integrationEndpoint?: string | null
      status?: SubsystemStatusValue
      operationalNote?: string | null
    },
  ): Promise<unknown> =>
    request(`/api/administration/subsystems/${encodeURIComponent(subsystemCode)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 6, TASK 19 — Annex A story 28, `app.agency_provider_mapping`
  // (the catalogue half of SECURITY.md S-027 / Q-30).
  // ─────────────────────────────────────────────────────────────────────

  agencyMappings: (
    params: { mappingAgencyCode?: string; providerAgencyCode?: string; sort?: string; dir?: string; limit?: number } = {},
  ): Promise<AgencyMappingListResponse> => {
    const query = new URLSearchParams()
    if (params.mappingAgencyCode !== undefined) query.set('mappingAgencyCode', params.mappingAgencyCode)
    if (params.providerAgencyCode !== undefined) query.set('providerAgencyCode', params.providerAgencyCode)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const suffix = query.toString()
    return request(`/api/administration/agency-mappings${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  agencyMappingExportUrl: (format: 'pdf' | 'xlsx'): string => `/api/administration/agency-mappings/export?format=${format}`,

  supersedeAgencyMapping: (
    mappingAgencyCode: string,
    body: { providerAgencyCode: string; effectiveFrom: string; note?: string; establish?: boolean },
  ): Promise<unknown> =>
    request(`/api/administration/agency-mappings/${encodeURIComponent(mappingAgencyCode)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 6, TASK 20 — Annex A story 31, `app.medical_scheme_code`.
  // ─────────────────────────────────────────────────────────────────────

  medicalSchemeCodes: (
    params: { category?: string; sort?: string; dir?: string; limit?: number } = {},
  ): Promise<MedicalSchemeCodeListResponse> => {
    const query = new URLSearchParams()
    if (params.category !== undefined) query.set('category', params.category)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const suffix = query.toString()
    return request(`/api/administration/medical-scheme-codes${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  medicalSchemeCodeExportUrl: (format: 'pdf' | 'xlsx'): string =>
    `/api/administration/medical-scheme-codes/export?format=${format}`,

  supersedeMedicalSchemeCode: (
    code: string,
    body: {
      effectiveFrom: string
      note?: string
      establish?: boolean
      description?: string
      category?: string
      coverageDetails?: string | null
    },
  ): Promise<unknown> =>
    request(`/api/administration/medical-scheme-codes/${encodeURIComponent(code)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 6, TASK 21 — Annex A story 29, `app.hsp_group`.
  // ─────────────────────────────────────────────────────────────────────

  hspGroups: (params: { status?: HspGroupStatusValue; sort?: string; dir?: string; limit?: number } = {}): Promise<HspGroupListResponse> => {
    const query = new URLSearchParams()
    if (params.status !== undefined) query.set('status', params.status)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const suffix = query.toString()
    return request(`/api/administration/hsp-groups${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  hspGroupExportUrl: (format: 'pdf' | 'xlsx'): string => `/api/administration/hsp-groups/export?format=${format}`,

  supersedeHspGroup: (
    code: string,
    body: {
      effectiveFrom: string
      note?: string
      establish?: boolean
      name?: string
      description?: string | null
      serviceCategories?: string | null
      status?: HspGroupStatusValue
      operationalNote?: string | null
    },
  ): Promise<unknown> =>
    request(`/api/administration/hsp-groups/${encodeURIComponent(code)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 6, TASK 21 — Annex A story 30, `app.hsp_group_membership`.
  // ─────────────────────────────────────────────────────────────────────

  hspGroupMemberships: (
    params: { hspAgencyCode?: string; groupCode?: string; sort?: string; dir?: string; limit?: number; offset?: number } = {},
  ): Promise<HspGroupMembershipListResponse> => {
    const query = new URLSearchParams()
    // ★★ 2026-08-18 — the page offset; see services/gateway/src/list-reachability.test.ts.
    if (params.offset !== undefined && params.offset > 0) query.set('offset', String(params.offset))
    if (params.hspAgencyCode !== undefined) query.set('hspAgencyCode', params.hspAgencyCode)
    if (params.groupCode !== undefined) query.set('groupCode', params.groupCode)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const suffix = query.toString()
    return request(`/api/administration/hsp-group-memberships${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  hspGroupMembershipExportUrl: (format: 'pdf' | 'xlsx'): string =>
    `/api/administration/hsp-group-memberships/export?format=${format}`,

  supersedeHspGroupMembership: (
    hspAgencyCode: string,
    body: { groupCode: string; serviceOfferings?: string | null; effectiveFrom: string; note?: string; establish?: boolean },
  ): Promise<unknown> =>
    request(`/api/administration/hsp-group-memberships/${encodeURIComponent(hspAgencyCode)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 6, TASK 22 — Annex A story 36, `app.mbim_record`.
  // ─────────────────────────────────────────────────────────────────────

  mbimRecords: (
    params: { entitlementType?: string; sort?: string; dir?: string; limit?: number; offset?: number } = {},
  ): Promise<MbimRecordListResponse> => {
    const query = new URLSearchParams()
    // ★★ 2026-08-18 — the page offset; see services/gateway/src/list-reachability.test.ts.
    if (params.offset !== undefined && params.offset > 0) query.set('offset', String(params.offset))
    if (params.entitlementType !== undefined) query.set('entitlementType', params.entitlementType)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const suffix = query.toString()
    return request(`/api/administration/mbim-records${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  mbimRecordExportUrl: (format: 'pdf' | 'xlsx'): string => `/api/administration/mbim-records/export?format=${format}`,

  supersedeMbimRecord: (
    officerId: string,
    body: { entitlementType: string; issuedOn: string; mbimNumber?: string; note?: string; establish?: boolean },
  ): Promise<unknown> =>
    request(`/api/administration/mbim-records/${encodeURIComponent(officerId)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 6, TASK 23 — Annex A story 32, `app.exclusion_list_entry`.
  // ⚠ Holds classified personal data (P2C11 §3.6) — see
  // `exclusion-list-repository.ts`'s own header for the masking rules these
  // calls surface, unchanged, to the screen.
  // ─────────────────────────────────────────────────────────────────────

  exclusionList: (
    params: { identifierType?: string; identifierValue?: string; sort?: string; dir?: string; limit?: number; offset?: number } = {},
  ): Promise<ExclusionListResponse> => {
    const query = new URLSearchParams()
    // ★★ 2026-08-18 — the page offset; see services/gateway/src/list-reachability.test.ts.
    if (params.offset !== undefined && params.offset > 0) query.set('offset', String(params.offset))
    if (params.identifierType !== undefined) query.set('identifierType', params.identifierType)
    if (params.identifierValue !== undefined) query.set('identifierValue', params.identifierValue)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const suffix = query.toString()
    return request(`/api/administration/exclusion-list${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  exclusionListExportUrl: (format: 'pdf' | 'xlsx'): string => `/api/administration/exclusion-list/export?format=${format}`,

  exclusionListEntry: (identifierType: string, identifierValue: string): Promise<{ entries: readonly unknown[] }> =>
    request(`/api/administration/exclusion-list/${encodeURIComponent(identifierType)}/${encodeURIComponent(identifierValue)}`),

  supersedeExclusionListEntry: (
    identifierType: string,
    identifierValue: string,
    body: { effectiveFrom: string; note?: string; establish?: boolean; exclusionCriteria?: string | null; exclusionReason?: string | null },
  ): Promise<unknown> =>
    request(
      `/api/administration/exclusion-list/${encodeURIComponent(identifierType)}/${encodeURIComponent(identifierValue)}/supersede`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 6, TASK 24 — Annex A story 33, `app.email_template`.
  // ⚠ TEMPLATE ADMINISTRATION ONLY — the outbox schema now exists
  // (`app.notification_delivery`, `054_notification_delivery.sql`), but no
  // transport is wired to it.
  // ─────────────────────────────────────────────────────────────────────

  emailTemplates: (
    params: { status?: EmailTemplateStatusValue; sort?: string; dir?: string; limit?: number } = {},
  ): Promise<EmailTemplateListResponse> => {
    const query = new URLSearchParams()
    if (params.status !== undefined) query.set('status', params.status)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const suffix = query.toString()
    return request(`/api/administration/email-templates${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  emailTemplateExportUrl: (format: 'pdf' | 'xlsx'): string => `/api/administration/email-templates/export?format=${format}`,

  supersedeEmailTemplate: (
    templateCode: string,
    body: {
      effectiveFrom: string
      note?: string
      establish?: boolean
      templateName?: string
      subjectLine?: string
      bodyContent?: string
      variables?: string | null
      category?: string | null
      status?: EmailTemplateStatusValue
    },
  ): Promise<unknown> =>
    request(`/api/administration/email-templates/${encodeURIComponent(templateCode)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 6, TASK 25 — Annex A story 34, `app.announcement`.
  // ─────────────────────────────────────────────────────────────────────

  announcements: (
    params: { priority?: AnnouncementPriorityValue; sort?: string; dir?: string; limit?: number } = {},
  ): Promise<AnnouncementListResponse> => {
    const query = new URLSearchParams()
    if (params.priority !== undefined) query.set('priority', params.priority)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const suffix = query.toString()
    return request(`/api/administration/announcements${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  announcementExportUrl: (format: 'pdf' | 'xlsx'): string => `/api/administration/announcements/export?format=${format}`,

  supersedeAnnouncement: (
    announcementCode: string,
    body: {
      effectiveFrom: string
      note?: string
      establish?: boolean
      title?: string
      content?: string
      priority?: AnnouncementPriorityValue
      targetAudience?: string | null
    },
  ): Promise<unknown> =>
    request(`/api/administration/announcements/${encodeURIComponent(announcementCode)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 6, TASK 26 — Annex A story 35, `app.special_agency_case`.
  // ⚠ Entitlement User only — see `mayReadSpecialAgencyCases`'s own header.
  // ─────────────────────────────────────────────────────────────────────

  specialAgencyCases: (
    params: { agencyCode?: string; officerId?: string; sort?: string; dir?: string; limit?: number } = {},
  ): Promise<SpecialAgencyCaseListResponse> => {
    const query = new URLSearchParams()
    if (params.agencyCode !== undefined) query.set('agencyCode', params.agencyCode)
    if (params.officerId !== undefined) query.set('officerId', params.officerId)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    const suffix = query.toString()
    return request(`/api/administration/special-agency-cases${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  specialAgencyCaseExportUrl: (format: 'pdf' | 'xlsx'): string => `/api/administration/special-agency-cases/export?format=${format}`,

  supersedeSpecialAgencyCase: (
    caseCode: string,
    body: {
      effectiveFrom: string
      note?: string
      establish?: boolean
      officerId?: string
      agencyCode?: string
      caseType?: string
      entitlementConfiguration?: string | null
    },
  ): Promise<unknown> =>
    request(`/api/administration/special-agency-cases/${encodeURIComponent(caseCode)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 10 (Task 35) — Annex A 40-47. Both endpoints already existed;
  // this is the first UI reading either. Read-only on this screen — the
  // existing `POST .../supersede` capability stays reachable only by an
  // authorised caller constructing the request directly, the same
  // narrowing several Phase 5 report routes already carry.
  // ─────────────────────────────────────────────────────────────────────

  referenceParameters: (): Promise<ReferenceParameterListResponse> => request('/api/reference'),

  /**
   * ★★ ADR 0011 — the PSD account directory, read-only.
   *
   * ⚠ **Keyset-paged with the SAME cursor `/api/cam/users` walks**, because the
   * route reads through the same repository function. `cursor` is always the
   * exact string a previous response's `nextCursor` carried and is never
   * constructed here.
   */
  accountDirectory: (
    params: { search?: string; cursor?: string; limit?: number; sort?: string; dir?: string } = {},
  ): Promise<AccountDirectoryResponse> => {
    const query = new URLSearchParams()
    if (params.search !== undefined && params.search.length > 0) query.set('search', params.search)
    if (params.cursor !== undefined) query.set('cursor', params.cursor)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    // ★★ 2026-08-24 — the sort. ⚠ The CURSOR carries the sort it was minted under,
    // and the server treats one minted under a different sort as no cursor at all —
    // so a header click has to reset the walk, which `AccountDirectoryPage` does.
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    const suffix = query.toString()
    return request(`/api/administration/accounts${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  /** ★★ ADR 0011 — CAM groups and their membership, read-only. Carries the A-46/Q-35 note. */
  accountGroups: (
    params: { search?: string; cursor?: string; sort?: string; dir?: string } = {},
  ): Promise<AccountGroupsResponse> => {
    const query = new URLSearchParams()
    if (params.search !== undefined && params.search.length > 0) query.set('search', params.search)
    if (params.cursor !== undefined) query.set('cursor', params.cursor)
    // ★★ 2026-08-24 — the sort. ⚠ The CURSOR carries the sort it was minted under,
    // and the server treats one minted under a different sort as no cursor at all —
    // so a header click has to reset the walk, which `AccountDirectoryPage` does.
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    const suffix = query.toString()
    return request(`/api/administration/account-groups${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  /**
   * ★★★ WAVE 12 (P4) — `asRecordedAt` REACHES THIS CALL AT LAST, and this is the
   * catalogue where it matters most.
   *
   * `GET /api/reference/:parameter` has accepted `?asRecordedAt=` since Task 15 and
   * this builder never sent it, so the pro-ration PARAMETERS — the numbers that
   * decide money — were the one catalogue with no way to ask *"what did the system
   * hold when this bill was computed?"*. That is the question an auditor asks about
   * a disputed bill, and until now the screen could not put it.
   *
   * ⚠ Two DIFFERENT time axes, and confusing them is the whole hazard:
   *   * `serviceDate` — WHICH RULE APPLIES to care given on that date;
   *   * `asRecordedAt` — WHAT THE SYSTEM KNEW at that instant.
   * A bill computed in March under a rate superseded in June needs the March
   * `asRecordedAt` and its own `serviceDate`. Asking with only one answers a
   * different question with full confidence.
   */
  /**
   * ★★★ WAVE 12 (P5) — the adjustment-indicator register against the published
   * file-exchange grammar, on one date of medical service (Annex A story 40).
   *
   * ⚠ No `parameter` argument, deliberately: reconciliation compares against a
   * mapping that exists for exactly one series, and a parameterised call would
   * have implied every parameter has a grammar to be reconciled against.
   */
  indicatorReconciliation: (serviceDate: string): Promise<IndicatorReconciliationResponse> =>
    request(`/api/reference/adjustment-indicators/reconciliation?${new URLSearchParams({ serviceDate }).toString()}`),

  referenceEntries: (parameter: string, serviceDate: string, asRecordedAt?: string): Promise<ReferenceEntriesResponse> => {
    const query = new URLSearchParams({ serviceDate })
    if (asRecordedAt !== undefined && asRecordedAt.length > 0) query.set('asRecordedAt', asRecordedAt)
    return request(`/api/reference/${encodeURIComponent(parameter)}?${query.toString()}`)
  },


  /**
   * ★★ WAVE 3 — Annex A 40-47's write half, from a screen for the first time.
   *
   * ⚠ **There is no edit-in-place, anywhere in this chain.** The route closes
   * the open window and opens a new one in one transaction, and
   * `reference_value_no_value_edit` (012_reference_data.sql) refuses any UPDATE
   * that touches a value, a key, a parameter or a start date — so this is not a
   * convention a caller could route around, it is what the schema permits.
   *
   * ⚠ `value: null` RETIRES the entry rather than saving an empty one. The two
   * are different events and the response says which happened.
   */
  supersedeReferenceValue: (
    parameter: string,
    body: {
      key: string
      effectiveFrom: string
      agencyCode?: string | null
      value?: { kind: 'CENTS'; cents: number } | { kind: 'WHOLE_DAYS'; days: number } | { kind: 'CODE'; code: string } | null
      note?: string
    },
  ): Promise<ReferenceSupersedeResponse> =>
    request(`/api/reference/${encodeURIComponent(parameter)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 10 (Task 37) — Annex A 5/6/8. Read-only: a batch is produced by
  // the emitting/publishing BATCH PROCESS, never from a screen.
  // ─────────────────────────────────────────────────────────────────────

  /**
    * ★★ `sort`/`dir` added 2026-08-24. This list was sorted IN THE BROWSER over a
    * response the route caps at 100 artefacts — see `SettlementBatchesPage`'s own
    * note where its local comparator used to be.
    */
  settlementBatches: (
    params: { sort?: string; dir?: string; offset?: number } = {},
  ): Promise<SettlementBatchListResponse> => {
    const query = new URLSearchParams()
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    // ★★ 2026-08-24 (Wave 18) — the walk this list did not have.
    if (params.offset !== undefined && params.offset > 0) query.set('offset', String(params.offset))
    const suffix = query.toString()
    return request(`/api/settlement-batches${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  /**
   * ★★ WAVE 1b. `POST /api/settlement-batches` — Annex A story 5.
   *
   * ⚠ No business date is sent. It is the SERVER's, in the Authority's
   * operating timezone: a browser computes "today" in the viewer's own zone,
   * and an officer in a different one would stamp a Payment Agency's artefact
   * with the wrong day.
   */
  emitSettlementBatch: (counterpartyCode: string): Promise<SettlementEmitResponse> =>
    request('/api/settlement-batches', { method: 'POST', body: JSON.stringify({ counterpartyCode }) }),

  /**
   * ★★★ WAVE 8 (Task 8) — `POST /api/outcome-files`, Annex A story 8's outcome
   * file, FROM A SCREEN.
   *
   * ⚠ **This one line is what closed the `⚠ NO SCREEN:` declaration in
   * `RTM.md`.** Wave 8 Task 1 gave `emitOutcome` its first real caller and the
   * only way to reach it was `curl`; `rtm-traceability.test.ts` decides
   * reachability by asking whether THIS FILE names the path, because this is the
   * one place the app builds a request. Until now it did not.
   *
   * ⚠ **No business date is sent, and no counterparty either.** The date is the
   * server's, in the Authority's operating timezone, for the reason
   * {@link emitSettlementBatch} states — and the provider is DERIVED from the
   * delivery inside `openOutcome`'s own SQL, never named by a caller, which is
   * what makes it impossible to address one provider's outcome file to another.
   * The delivery id is the whole request.
   */
  outcomeFile: (inboundFileId: string): Promise<OutcomeFileResponse> =>
    request('/api/outcome-files', { method: 'POST', body: JSON.stringify({ inboundFileId }) }),

  /**
   * ★★ PAGED 2026-08-20 (Wave 8). The unpaged form measured 267,326 bytes against the
   * demonstration estate's largest batch — 5.35× the 50,000-byte cap P2C3 §7.1.6
   * states — and nothing measured it.
   *
   * ⚠ **No `limit` parameter, deliberately.** The page size is the SERVER's
   * (`SETTLEMENT_MEMBER_PAGE_SIZE`, derived from a measured byte budget) and it comes
   * back in `paging.limit`. A `?limit=` the screen chose would be a second copy of
   * that arithmetic on the client, which is Wave 8's constraint 3 — the same reason
   * `accessControlMatrix` reads its page sizes out of the response.
   */
  /** ★★ `sort`/`dir` added 2026-08-24 — this membership had eight plain headers. */
  settlementBatchMembers: (
    id: string,
    offset = 0,
    params: { sort?: string; dir?: string } = {},
  ): Promise<SettlementBatchMembersResponse> => {
    const query = new URLSearchParams()
    if (offset > 0) query.set('offset', String(offset))
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    const suffix = query.toString()
    return request(
      `/api/settlement-batches/${encodeURIComponent(id)}/members${suffix.length > 0 ? `?${suffix}` : ''}`,
    )
  },

  settlementBatchExportUrl: (format: 'pdf' | 'xlsx'): string => `/api/settlement-batches/export?format=${format}`,

  settlementBatchMembersExportUrl: (id: string, format: 'pdf' | 'xlsx'): string =>
    `/api/settlement-batches/${encodeURIComponent(id)}/members/export?format=${format}`,

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 10 (Annex A story 51) — Financial Counselling cost estimation.
  // Never a bill: no reference is read or created, nothing is persisted.
  // ─────────────────────────────────────────────────────────────────────

  costEstimate: (input: {
    officerId: string
    serviceDate: string
    billAmountCents: number
    wardClass?: string
  }): Promise<CostEstimateResponse> =>
    request('/api/cost-estimate', { method: 'POST', body: JSON.stringify(input) }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 10 (Annex A stories 24, 25) — online help and the user manual.
  // ─────────────────────────────────────────────────────────────────────

  helpTopics: (query?: string): Promise<HelpTopicsResponse> =>
    request(`/api/help/topics${query !== undefined && query.length > 0 ? `?query=${encodeURIComponent(query)}` : ''}`),

  helpTopic: (topicId: string): Promise<HelpTopicResponse> => request(`/api/help/topics/${encodeURIComponent(topicId)}`),

  helpManualUrl: (format: 'pdf' | 'xlsx'): string => `/api/help/manual?format=${format}`,

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 10 (Annex A story 49) — print CS card information.
  // ─────────────────────────────────────────────────────────────────────

  csCard: (officerId: string, asOfDate?: string): Promise<CsCardResponse> =>
    request(
      `/api/cs-card/${encodeURIComponent(officerId)}${asOfDate !== undefined && asOfDate.length > 0 ? `?asOfDate=${encodeURIComponent(asOfDate)}` : ''}`,
    ),

  csCardExportUrl: (officerId: string, format: 'pdf' | 'xlsx', asOfDate?: string): string => {
    const query = new URLSearchParams({ format })
    if (asOfDate !== undefined && asOfDate.length > 0) query.set('asOfDate', asOfDate)
    return `/api/cs-card/${encodeURIComponent(officerId)}/export?${query.toString()}`
  },

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 10 (Annex A story 50) — the notification RECORD and its
  // trigger. Nothing here is ever delivered.
  // ─────────────────────────────────────────────────────────────────────

  notificationRules: (): Promise<NotificationRulesResponse> => request('/api/notifications/rules'),

  supersedeNotificationRule: (
    eventType: string,
    body: { effectiveFrom: string; recipientList?: string; templateCode?: string | null; enabled?: boolean; note?: string; establish?: boolean },
  ): Promise<{ outcome: string }> =>
    request(`/api/notifications/rules/${encodeURIComponent(eventType)}/supersede`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * ★★ `offset` added 2026-08-18. One row per notifiable bill event against a
   * cap of 100, with P2C1 §15 putting bills at 4,879 a day — this surface could
   * fall two days behind on its first day and nothing could read past it.
   */
  notificationLog: (
    limit?: number,
    offset?: number,
    // ★★ 2026-08-24 — the sort. ⚠ An OBJECT rather than two more positional
    // arguments: this signature is already `(limit, offset)`, and a fifth
    // positional would make `notificationLog(undefined, undefined, 'x', 'y')` the
    // shape of a plain sort call, which is where a transposed pair goes unnoticed.
    sort?: { readonly sort?: string; readonly dir?: string },
  ): Promise<NotificationLogResponse> => {
    const query = new URLSearchParams()
    if (limit !== undefined) query.set('limit', String(limit))
    if (offset !== undefined && offset > 0) query.set('offset', String(offset))
    if (sort?.sort !== undefined) query.set('sort', sort.sort)
    if (sort?.dir !== undefined) query.set('dir', sort.dir)
    const suffix = query.toString()
    return request(`/api/notifications/log${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  recordEcsEntitlementEvent: (officerId: string): Promise<NotificationTriggerResponse> =>
    request('/api/notifications/ecs-entitlement-events', { method: 'POST', body: JSON.stringify({ officerId }) }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ TASK 7 (2026-08-15 notification-delivery wave) — the operator's own
  // read of the outbox Task 5 built, and the two actions it exposes. Plain
  // `request<T>` casts, like every other route above: `notificationDelivery`
  // on each of these four responses is the one field a caller must narrow
  // through `readNotificationDelivery`/`isNotificationDelivery`
  // (`@prs2/contracts`) before trusting — see `types.ts`'s own note on
  // `NotificationDeliveryListResponse` for why that happens at the READING
  // call site (`NotificationsPage.tsx`) rather than here.
  //
  // ⚠ A 409 from `outboxRun`/`requeueNotificationDelivery` (a lost claim; a
  // BOUNCED or not-ABANDONED refusal) surfaces as an `ApiError` exactly like
  // any other non-2xx response — `ApiError` carries only `status`/`message`,
  // so the STRUCTURED `result`/`partialResult` a `CLAIM_LOST` 409 also carries
  // is not reachable through this call. That is an accepted, narrow gap: widening
  // `ApiError` to carry an arbitrary body would change every OTHER call site's
  // error contract for one rare race (a claim lease outrun mid-batch), and the
  // 409's own message already points the operator at the fix — reload the list
  // to see what committed, then run the outbox again.
  // ─────────────────────────────────────────────────────────────────────

  notificationDeliveries: (
    params: { status?: string; sort?: string; dir?: string; limit?: number; offset?: number } = {},
  ): Promise<NotificationDeliveryListResponse> => {
    const query = new URLSearchParams()
    if (params.status !== undefined) query.set('status', params.status)
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    // ★★ 2026-08-18 — the page offset. ONE ROW PER RECIPIENT per event against
    // a cap of 30: the worst ratio of any list surface in the product.
    if (params.offset !== undefined && params.offset > 0) query.set('offset', String(params.offset))
    const suffix = query.toString()
    return request(`/api/notifications/deliveries${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  notificationDeliveryAttempts: (id: string): Promise<NotificationDeliveryAttemptsResponse> =>
    request(`/api/notifications/deliveries/${encodeURIComponent(id)}/attempts`),

  runNotificationOutbox: (): Promise<NotificationOutboxRunResponse> =>
    request('/api/notifications/outbox/run', { method: 'POST' }),

  requeueNotificationDelivery: (id: string): Promise<NotificationRequeueResponse> =>
    request(`/api/notifications/deliveries/${encodeURIComponent(id)}/requeue`, { method: 'POST' }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 11 (Annex A story 39) — the scoped FAQ.
  // ─────────────────────────────────────────────────────────────────────

  faqEntries: (): Promise<FaqListResponse> => request('/api/faq'),

  askFaq: (question: string): Promise<FaqAskResponse> =>
    request('/api/faq/ask', { method: 'POST', body: JSON.stringify({ question }) }),

  // ─────────────────────────────────────────────────────────────────────
  // ★ PHASE 11 (Annex A story 48) — local support tickets. No live WOG
  // ITSM connection exists — externalTicketRef is always null.
  // ─────────────────────────────────────────────────────────────────────

  createSupportTicket: (input: {
    subject: string
    description: string
    priority?: string
    systemComponent?: string
  }): Promise<CreateSupportTicketResponse> => request('/api/support-tickets', { method: 'POST', body: JSON.stringify(input) }),

  /**
   * ★★ `limit`/`offset` added 2026-08-18 — this table grows with use and is
   * never pruned, and it was capped at 100 with no way past.
   */
  supportTickets: (
    params: { limit?: number; offset?: number; sort?: string; dir?: string } = {},
  ): Promise<SupportTicketListResponse> => {
    const query = new URLSearchParams()
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.offset !== undefined && params.offset > 0) query.set('offset', String(params.offset))
    // ★★ 2026-08-24 — the sort. ⚠ Both or neither, and never `undefined` as a
    // string: `sortParams` emits the pair together, so a `?dir=` with no `?sort=`
    // cannot be produced from this client.
    if (params.sort !== undefined) query.set('sort', params.sort)
    if (params.dir !== undefined) query.set('dir', params.dir)
    const suffix = query.toString()
    return request(`/api/support-tickets${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  supportTicket: (id: string): Promise<SupportTicketDetailResponse> => request(`/api/support-tickets/${encodeURIComponent(id)}`),

  transitionSupportTicket: (id: string, to: string, note?: string): Promise<{ ticket: SupportTicket }> =>
    request(`/api/support-tickets/${encodeURIComponent(id)}/transition`, {
      method: 'POST',
      body: JSON.stringify({ to, ...(note !== undefined && { note }) }),
    }),

  // ─────────────────────────────────────────────────────────────────────
  // ★★ ANNEX A 22 — the MSO pro-ration rule engine (routes/mso-rules.ts).
  // PSD only: every route below answers 403 to anyone else.
  // ─────────────────────────────────────────────────────────────────────

  msoRules: (): Promise<MsoRuleListResponse> => request('/api/mso-rules'),

  msoRuleVersions: (ruleKey: string): Promise<MsoRuleVersionsResponse> =>
    request(`/api/mso-rules/${encodeURIComponent(ruleKey)}/versions`),

  msoRuleVersion: (id: string): Promise<MsoRuleVersionResponse> =>
    request(`/api/mso-rules/versions/${encodeURIComponent(id)}`),

  createMsoDraft: (body: {
    ruleKey: string
    effectiveFrom: string
    name?: string
    purpose?: string
    definition?: unknown
    basedOnVersionId?: string
  }): Promise<{ version: MsoRuleVersion }> =>
    request('/api/mso-rules/drafts', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * `expectedHash` is the definition hash the editor LOADED: a save over a draft someone else changed since is
   * refused 409 STALE_EDIT (never overwritten silently). `definition` may be omitted for a date-only edit.
   */
  editMsoDraft: (
    id: string,
    body: { definition?: unknown; effectiveFrom?: string; expectedHash?: string },
  ): Promise<{ version: MsoRuleVersion }> =>
    request(`/api/mso-rules/versions/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),

  addMsoTestCase: (
    ruleKey: string,
    body: {
      caseId: string
      description: string
      input: unknown
      expectedOfficerCents?: number
      expectedGovernmentCents?: number
      expectedRefusalKind?: string
    },
  ): Promise<{ ok: true }> =>
    request(`/api/mso-rules/${encodeURIComponent(ruleKey)}/test-cases`, { method: 'POST', body: JSON.stringify(body) }),

  /** Remove a case an officer added. 400 INVALID_REQUEST for an Authority pack case; 404 when it is not there. */
  deleteMsoTestCase: (ruleKey: string, caseId: string): Promise<{ ok: true }> =>
    request(`/api/mso-rules/${encodeURIComponent(ruleKey)}/test-cases/${encodeURIComponent(caseId)}`, { method: 'DELETE' }),

  testMsoDraft: (id: string): Promise<MsoTestRunResponse> =>
    request(`/api/mso-rules/versions/${encodeURIComponent(id)}/test`, { method: 'POST', body: JSON.stringify({}) }),

  /** ⚠ Irreversible: a second PSD officer activates, with a provenance note. Callers confirm first. */
  activateMsoDraft: (id: string, note: string): Promise<{ version: MsoRuleVersion; superseded: MsoRuleVersion | null }> =>
    request(`/api/mso-rules/versions/${encodeURIComponent(id)}/activate`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  /** ⚠ Irreversible: bills dated after `effectiveUntil` are refused, not priced. Callers confirm first. */
  retireMsoRule: (ruleKey: string, body: { effectiveUntil: string; note: string }): Promise<{ version: MsoRuleVersion }> =>
    request(`/api/mso-rules/${encodeURIComponent(ruleKey)}/retire`, { method: 'POST', body: JSON.stringify(body) }),

  /**
   * ★ Calculate and RECORD. Resolves with the body for a 200 AND for the 400 / 422
   * the route sends when it refuses: a refusal is a stored, explained result (its
   * trace and calculation id are the point of the screen), not a transport failure.
   * Any other failure still throws `ApiError`, so a 403 or a dropped session is
   * reported as what it is.
   */
  calculateMso: async (input: unknown): Promise<MsoCalculateResponse> => {
    try {
      return await request<MsoCalculateResponse>('/api/mso-calculations', { method: 'POST', body: JSON.stringify(input) })
    } catch (error) {
      if (error instanceof ApiError && (error.status === 400 || error.status === 422)) {
        const evaluation = error.detail['evaluation']
        const calculation = error.detail['calculation']
        if (typeof evaluation === 'object' && evaluation !== null && typeof calculation === 'object' && calculation !== null) {
          return { calculation: calculation as MsoCalculation, evaluation: evaluation as MsoEvaluation }
        }
      }
      throw error
    }
  },

  msoCalculations: (
    params: { ruleKey?: string; outcome?: string; from?: string; to?: string; limit?: number; offset?: number; sort?: string; dir?: string } = {},
  ): Promise<MsoCalculationListResponse> => {
    const query = new URLSearchParams()
    if (params.ruleKey !== undefined && params.ruleKey.length > 0) query.set('ruleKey', params.ruleKey)
    if (params.outcome !== undefined && params.outcome.length > 0) query.set('outcome', params.outcome)
    if (params.from !== undefined && params.from.length > 0) query.set('from', params.from)
    if (params.to !== undefined && params.to.length > 0) query.set('to', params.to)
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.offset !== undefined && params.offset > 0) query.set('offset', String(params.offset))
    if (params.sort !== undefined && params.sort.length > 0) {
      query.set('sort', params.sort)
      if (params.dir !== undefined && params.dir.length > 0) query.set('dir', params.dir)
    }
    const suffix = query.toString()
    return request(`/api/mso-calculations${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  msoCalculation: (id: string): Promise<MsoCalculationDetailResponse> =>
    request(`/api/mso-calculations/${encodeURIComponent(id)}`),

  /**
   * A URL, not a fetch (the route answers with an attachment). Carries the enquiry's CURRENT filters, so a download
   * is exactly what is on screen and never the whole record.
   */
  msoCalculationsExportUrl: (
    format: 'pdf' | 'xlsx',
    filters: { ruleKey?: string; outcome?: string; from?: string; to?: string; sort?: string; dir?: string } = {},
  ): string => {
    const query = new URLSearchParams({ format })
    for (const key of ['ruleKey', 'outcome', 'from', 'to'] as const) {
      const value = filters[key]
      if (value !== undefined && value.length > 0) query.set(key, value)
    }
    // The order on screen travels with the download, so the file lists rows as the officer sees them.
    if (filters.sort !== undefined && filters.sort.length > 0) {
      query.set('sort', filters.sort)
      if (filters.dir !== undefined && filters.dir.length > 0) query.set('dir', filters.dir)
    }
    return `/api/mso-calculations/export?${query.toString()}`
  },

  msoRulesExportUrl: (format: 'pdf' | 'xlsx'): string => `/api/mso-rules/export?format=${format}`,

  // Q-54: the read-only comparison of the engine with the flat-rate path.
  msoComparison: (params: { differsOnly?: boolean; limit?: number; offset?: number; sort?: string; dir?: string } = {}): Promise<MsoComparisonResponse> => {
    const query = new URLSearchParams()
    if (params.differsOnly === true) query.set('differsOnly', 'true')
    if (params.limit !== undefined) query.set('limit', String(params.limit))
    if (params.offset !== undefined && params.offset > 0) query.set('offset', String(params.offset))
    if (params.sort !== undefined && params.sort.length > 0) {
      query.set('sort', params.sort)
      if (params.dir !== undefined && params.dir.length > 0) query.set('dir', params.dir)
    }
    const suffix = query.toString()
    return request(`/api/mso-comparison${suffix.length > 0 ? `?${suffix}` : ''}`)
  },

  /** One bill's comparison. Resolves null when the bill has no MSO basis (404), because that is a state, not a fault. */
  msoBillComparison: async (reference: string): Promise<MsoBillComparisonResponse | null> => {
    try {
      return await request<MsoBillComparisonResponse>(`/api/bills/${encodeURIComponent(reference)}/mso-comparison`)
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  },

  recordMsoBasis: (reference: string, body: MsoBasisInput): Promise<MsoRecordBasisResponse> =>
    request(`/api/bills/${encodeURIComponent(reference)}/mso-basis`, { method: 'PUT', body: JSON.stringify(body) }),
}
